import React from "react";
import { useTranslation } from "react-i18next";
import { AvForm, AvGroup } from "availity-reactstrap-validation";
import { useSnapshot } from "valtio";
import { useSelector, useDispatch } from "react-redux";
import classnames from "classnames";
import { StyledLabel, StyledTextInput } from "@aureskonnect/react-ui";

import { store } from "@components/VerticalLayout/store";
import { ConfirmationEditMessage } from "@components/Common/ConfirmationEditMessage";
import { CustomCardBody } from "@components/Common/CustomCardStyled";
import { getUuidUser } from "@helpers/getUuidUser";

import { useFetchGlobal } from "../../../../../api/hooks";
import { CESAR_LOYALTY, FIDELITY_POINTS_BLOCK_SIZE } from "@constants/index";

import { RootState, AppDispatch } from "../../../../../redux/store";
import { setIsEdited } from "../../../../../redux/slices/fidelitySlice";

import { FidelityFormProps, INPUT_ERROR_STYLE } from "./types";
import { useFidelityFormState } from "./useFidelityFormState";
import { useFidelityExistence } from "./useFidelityExistence";
import { useFetchModeOfSale } from "./useFetchModeOfSale";
import { handleValidate, addFidelity, editFidelity } from "./handlers";

import FormHeader from "./components/FormHeader";
import LoyaltyTypeSelect from "./components/LoyaltyTypeSelect";
import LoyaltyVariantSection from "./components/LoyaltyVariantSection";
import RemarkField from "./components/RemarkField";
import FormFooter from "./components/FormFooter";

import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";
import "../index.css";
import { ConfirmationAssociationModalWrapper } from "../ConfirmationAssociationModalWrapper";
import { ConfirmationModificationMessageWrapper } from "../ConfirmationModificationMessageWrapper";

export default function FidelityFormWrapper({
    setIsAddFidelityClicked,
    isAddFidelityClicked,
    setDataIsUpdated,
    dataFidelity,
    isAddNewFidelityButtonClicked,
    setDataFidelity,
    setLocalFilterActive,
    fidelityChoiceIndex,
    setFidelityChoiceIndex,
    isConsult,
    setIsConsult,
}: FidelityFormProps) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { userID, franchiseID, shopID, oneShop, operatorID } = useSnapshot(
        store
    );
    const numberActive = useSelector((s: RootState) => s.fidelity.numberActive);
    const shopsFidelityData = useSelector(
        (s: RootState) => s.fidelity.shopsFidelityData
    );
    const fidelityData = useSelector((s: RootState) => s.fidelity.fidelityData);
    const fidelityFranchise = useSelector(
        (s: RootState) => s.fidelity.fidelityFranchise
    );
    const dataArchive = useSelector((s: RootState) => s.fidelity.dataArchive);
    const dataFranchiseArchive = useSelector(
        (s: RootState) => s.fidelity.dataFranchiseArchive
    );
    const isActionsClicked = useSelector(
        (s: RootState) => s.fidelity.isActionsClicked
    );
    const isEdited = useSelector((s: RootState) => s.fidelity.isEdited);
    const dispatch = useDispatch<AppDispatch>();
    const dispatchSetIsEdited = React.useCallback(
        (v: boolean): void => {
            dispatch(setIsEdited(v));
        },
        [dispatch]
    );

    const shopId = oneShop ? shopID : "0";
    const uuidUser = getUuidUser();

    const saleBO = React.useMemo(
        () => [t("In delivery"), t("Dine-in"), t("Takeaway"), t("Restaurant")],
        [t]
    );

    const state = useFidelityFormState({
        dataFidelity,
        t,
        saleBO,
        isConsult,
        isAddNewFidelityButtonClicked,
        isAddFidelityClicked,
        setFidelityChoiceIndex,
    });
    const { title, values, flags, errors, setValue, setError } = state;

    const {
        isExist,
        franchiseMatch,
        checkIfFidelityFranchiseExist,
        checkIfFidelityFranchiseExistAndArchive,
    } = useFidelityExistence({
        values,
        dataFidelity,
        fidelityData,
        dataArchive: dataArchive as any[],
        fidelityFranchise,
        dataFranchiseArchive: dataFranchiseArchive as any[],
        oneShop,
        t,
    });

    const fetchModeOfSale = useFetchModeOfSale({
        userID,
        franchiseID,
        shopId,
        saleBO,
        fetchGlobal,
        setSortedVisibility: (v: any) => setValue("sortedVisibility", v),
    });

    const [isModalOpened, setIsModalOpened] = React.useState(false);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isModalModifiedOpened, setIsModalModifiedOpened] = React.useState(
        false
    );
    const [
        isValidateButtonEnabled,
        setIsValidateButtonEnabled,
    ] = React.useState(true);
    const [dataEdited, setDataEdited] = React.useState<any>();
    const [associated, setAssociated] = React.useState<any[]>([]);

    const isFidelityNumberInValid =
        values.numberFidelityPoints !== "" &&
        Number(values.numberFidelityPoints) % FIDELITY_POINTS_BLOCK_SIZE !== 0;

    React.useEffect(() => {
        const result: any[] = [];
        if (shopsFidelityData.data !== undefined) {
            shopsFidelityData.data.forEach((element: any) => {
                element.subRows?.forEach((el: any) => {
                    if (el.Id === dataFidelity?.Id) {
                        result.push({ ...el });
                    }
                });
            });
        }
        setAssociated(result);
        // Why: re-derive `associated` only on form navigation events (open/close/row
        // change) — not on `shopsFidelityData` ref changes which fire on unrelated
        // Redux mutations. `setAssociated` is a stable setState identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataFidelity, isAddNewFidelityButtonClicked, isAddFidelityClicked]);

    const resetForm = React.useCallback(() => {
        setValue("designation", "");
        setValue("choiceFidelity", "");
        setValue("authenticationMode", []);
    }, [setValue]);

    const onAdd = () => {
        addFidelity({
            values,
            flags,
            fidelityChoiceIndex,
            shopId,
            userID,
            franchiseID,
            operatorID,
            uuidUser,
            oneShop,
            numberActive,
            fidelityData,
            fidelityFranchise,
            isFranchiseExist: checkIfFidelityFranchiseExist(),
            isFranchiseExistAndArchived: checkIfFidelityFranchiseExistAndArchive(),
            fetchGlobal,
            t,
            setIsValidateButtonEnabled,
            setIsModalModifiedOpened,
            setIsAddFidelityClicked,
            isAddFidelityClicked,
            setDataFidelity,
            setLocalFilterActive,
            setDataIsUpdated,
            setFidelityChoiceIndex,
            resetForm,
        });
    };

    const onEdit = () => {
        editFidelity({
            values,
            flags,
            fidelityChoiceIndex,
            shopId,
            userID,
            franchiseID,
            operatorID,
            uuidUser,
            oneShop,
            dataFidelity,
            shopsFidelityData,
            fetchGlobal,
            t,
            setIsValidateButtonEnabled,
            setIsModalOpen,
            setIsAddFidelityClicked,
            setDataEdited,
        });
    };

    const onValidate = () => {
        handleValidate({
            state,
            isEdited,
            isExist,
            isFidelityNumberInValid,
            oneShop,
            dataFidelity,
            isModalOpened,
            setIsModalOpened,
            onAdd,
            onEdit,
            t,
        });
    };

    return (
        <React.Fragment>
            <AvForm className="card">
                <FormHeader
                    title={title}
                    dataFidelity={dataFidelity}
                    isEdited={isEdited}
                    isActionsClicked={isActionsClicked}
                    isAddFidelityClicked={isAddFidelityClicked}
                    setIsAddFidelityClicked={setIsAddFidelityClicked}
                    setDataFidelity={setDataFidelity}
                    setDataIsUpdated={setDataIsUpdated}
                />

                <CustomCardBody
                    className="pl-3 pr-5"
                    style={{ maxHeight: "470px", overflowY: "auto" }}
                >
                    <LoyaltyTypeSelect
                        state={state}
                        isConsult={isConsult}
                        isExist={isExist}
                        oneShop={oneShop}
                        fidelityData={fidelityData}
                        dataFidelity={dataFidelity}
                        setFidelityChoiceIndex={setFidelityChoiceIndex}
                        fetchModeOfSale={fetchModeOfSale}
                        t={t}
                    />

                    {/* Hidden designation field kept for Availity validation */}
                    <AvGroup style={{ display: "none" }}>
                        <StyledLabel
                            className="required__clz mt-3"
                            htmlFor="name"
                        >
                            {t("Display name")}
                        </StyledLabel>
                        <StyledTextInput
                            className={classnames("fed_inp_FRname", {
                                input__clz: errors.designation || isExist,
                                "not-allowed-icon__clz":
                                    dataFidelity?.Id !== undefined,
                            })}
                            autocomplete="off"
                            id="Display name"
                            name="Display name"
                            placeholder={t("Write")}
                            type="text"
                            onChange={(e: any) => {
                                setValue("designation", e.target.value.trim());
                                dispatchSetIsEdited(true);
                                setError("designation", false);
                            }}
                            value={values.designation}
                        />
                        {errors.designation ? (
                            <div style={INPUT_ERROR_STYLE}>
                                {t("Please enter a display name")}
                            </div>
                        ) : null}
                        {isExist ? (
                            <div style={INPUT_ERROR_STYLE}>
                                {values.choiceFidelity.startsWith(
                                    t("Fidelity Softavera")
                                )
                                    ? t(
                                          "Display name, loyalty type and group combination already exists"
                                      )
                                    : t(
                                          "The display name, fidelity type combination already exists"
                                      )}
                            </div>
                        ) : null}
                    </AvGroup>

                    <LoyaltyVariantSection
                        state={state}
                        fidelityChoiceIndex={fidelityChoiceIndex}
                        oneShop={oneShop}
                        isConsult={isConsult}
                        isExist={isExist}
                        isFidelityNumberInValid={isFidelityNumberInValid}
                        dataFidelity={dataFidelity}
                        setIsEdited={dispatchSetIsEdited}
                    />

                    {fidelityChoiceIndex !== CESAR_LOYALTY ? (
                        <RemarkField
                            remark={values.remark}
                            setRemark={(v: string) => setValue("remark", v)}
                            setIsEdited={dispatchSetIsEdited}
                            isConsult={isConsult}
                            t={t}
                        />
                    ) : null}
                </CustomCardBody>

                <FormFooter
                    isEdited={isEdited}
                    isActionsClicked={isActionsClicked}
                    isAddFidelityClicked={isAddFidelityClicked}
                    isConsult={isConsult}
                    isValidateButtonEnabled={isValidateButtonEnabled}
                    oneShop={oneShop}
                    fidelityChoiceIndex={fidelityChoiceIndex}
                    onValidate={onValidate}
                    setIsAddFidelityClicked={setIsAddFidelityClicked}
                    setDataFidelity={setDataFidelity}
                    setDataIsUpdated={setDataIsUpdated}
                    setIsConsult={setIsConsult}
                    t={t}
                />
            </AvForm>

            <ConfirmationEditMessage
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                setIsAddClicked={setIsAddFidelityClicked}
                setEditedData={setDataFidelity}
            />

            {isModalOpen && (
                <ConfirmationModificationMessageWrapper
                    isModalOpened={isModalOpen}
                    setIsModalOpened={setIsModalOpen}
                    setIsEdited={dispatchSetIsEdited}
                    setDataIsUpdated={setDataIsUpdated}
                    dataFidelity={dataEdited}
                    associated={associated}
                    setIsValidateButtonEnabled={setIsValidateButtonEnabled}
                    setIsAddFiscalYearClicked={setIsAddFidelityClicked}
                    isAddFiscalYearClicked={isAddFidelityClicked}
                />
            )}

            {isModalModifiedOpened && franchiseMatch.Id && (
                <ConfirmationAssociationModalWrapper
                    isModalOpened={isModalModifiedOpened}
                    setIsModalOpened={setIsModalModifiedOpened}
                    setDataIsUpdated={setDataIsUpdated}
                    data={{
                        [String(franchiseMatch.Id)]: {
                            designation: values.designation,
                            type: values.choiceFidelity,
                            number_fidelity_points: values.numberFidelityPoints,
                            money_correspondence: values.moneyCorrespondence,
                            devise: franchiseMatch.devise ?? "",
                            pays: franchiseMatch.pays ?? "",
                            authentication_mode: values.authenticationMode.join(
                                ","
                            ),
                            sortedVisibility: values.sortedVisibility,
                            Login: values.login,
                            password: values.password,
                            note:
                                values.remark === ""
                                    ? franchiseMatch.note
                                    : values.remark,
                            isActive: false,
                            isArchived: false,
                            shopId,
                            dashboardLink: values.dashboardLink,
                            user: values.user,
                            apiKey: values.apiKey,
                            auresKonnectGroup: values.auresKonnectGroup,
                            isCentralizationActive:
                                flags.isCentralizationActive,
                            isVisibiliteActive: flags.isVisibiliteActive,
                            fidelityChoiceIndex,
                        },
                    }}
                    setDesignation={(v: string) => setValue("designation", v)}
                    setChoiceFidelity={(v: string) =>
                        setValue("choiceFidelity", v)
                    }
                    setAuthenticationMode={(v: string[]) =>
                        setValue("authenticationMode", v)
                    }
                    setIsAddFidelityClicked={setIsAddFidelityClicked}
                />
            )}
        </React.Fragment>
    );
}
